import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isApiError } from "@/data/api/client";
import { paymentAccountRepo } from "@/data/repos/paymentAccountRepo";
import { useUserBusiness } from "@/features/settings/hooks/useUserBusiness";

/** Scoped by business because the app-wide QueryClient outlives a sign-out: an
 *  unscoped key hands the next partner the previous one's account. */
export function paymentAccountQueryKey(businessId: number | null) {
  return ["payment-account", businessId] as const;
}

/** `leaving` is the phase no request is pending in any more and the control
 *  still must not be pressed: the browser has been told to go to Stripe and has
 *  not arrived yet. */
type StripeHandoffPhase = "idle" | "connecting" | "leaving";

const onboardingUnavailableMessage = "Ověření se teď nepodařilo otevřít. Zkuste to prosím znovu.";

/** `location.assign("")` reloads this page and a relative path stays inside
 *  the portal — both would be announced as a departure for Stripe. Only an
 *  absolute https link is one the browser can actually leave on. */
function isHostedOnboardingUrl(url: unknown): url is string {
  return typeof url === "string" && url.startsWith("https://");
}

export function usePaymentAccount() {
  const queryClient = useQueryClient();
  const { businessId, isLoading: isLoadingBusiness } = useUserBusiness();
  /** Mirrored into a ref because two clicks in one frame both read the render's
   *  state before React re-renders, and the second one must not mint a second
   *  account. */
  const handoffPhaseNow = useRef<StripeHandoffPhase>("idle");
  const [handoffPhase, setHandoffPhase] = useState<StripeHandoffPhase>("idle");

  function enterHandoffPhase(phase: StripeHandoffPhase) {
    handoffPhaseNow.current = phase;
    setHandoffPhase(phase);
  }

  function abandonHandoff(message: string) {
    enterHandoffPhase("idle");
    toast.error(message);
  }

  /** A partner who presses Back in the hosted form gets this page out of the
   *  browser's back-forward cache with its React state intact, so a `leaving`
   *  phase nothing resets would leave them looking at a dead button. What they
   *  did in Stripe meanwhile is also unknown, hence the re-read. */
  useEffect(() => {
    function releaseOnRestore(event: PageTransitionEvent) {
      if (!event.persisted) return;
      handoffPhaseNow.current = "idle";
      setHandoffPhase("idle");
      void queryClient.invalidateQueries({ queryKey: paymentAccountQueryKey(businessId) });
    }
    window.addEventListener("pageshow", releaseOnRestore);
    return () => window.removeEventListener("pageshow", releaseOnRestore);
  }, [queryClient, businessId]);

  const accountQuery = useQuery({
    queryKey: paymentAccountQueryKey(businessId),
    queryFn: () => paymentAccountRepo.get(),
    enabled: businessId !== null,
  });

  const startOnboarding = useMutation({
    mutationFn: () => paymentAccountRepo.startOnboarding(),
    onSuccess: ({ url }) => {
      if (!isHostedOnboardingUrl(url)) return abandonHandoff(onboardingUnavailableMessage);
      enterHandoffPhase("leaving");
      try {
        window.location.assign(url);
      } catch {
        abandonHandoff(onboardingUnavailableMessage);
      }
    },
    onError: (error) => {
      if (isApiError(error, "NO_CONNECTED_ACCOUNT")) {
        void queryClient.invalidateQueries({ queryKey: paymentAccountQueryKey(businessId) });
        return abandonHandoff("Podnik nemá připojený účet Stripe. Ověření za vás nemůžeme spustit.");
      }
      abandonHandoff(onboardingUnavailableMessage);
    },
  });

  /** Creation and the first link are one action from the partner's side, so a
   *  successful create walks straight on into the hosted form. A link that
   *  fails afterwards is not a failed creation: the account exists, the cached
   *  stage says so, and the retry is the onboarding button that stage offers. */
  const createAccount = useMutation({
    mutationFn: () => paymentAccountRepo.create(),
    onSuccess: (created) => {
      queryClient.setQueryData(paymentAccountQueryKey(businessId), created);
      startOnboarding.mutate();
    },
    /** The conflict stays locked until the re-read lands: releasing it against a
     *  cache that still says `no-account` re-offers creation, and every further
     *  press is another POST answered by another 409. */
    onError: async (error) => {
      if (isApiError(error, "ACCOUNT_EXISTS")) {
        toast.error("Účet už mezitím vznikl. Načetli jsme jeho stav znovu.");
        try {
          await queryClient.invalidateQueries({ queryKey: paymentAccountQueryKey(businessId) });
        } finally {
          enterHandoffPhase("idle");
        }
        return;
      }
      enterHandoffPhase("idle");
      if (isApiError(error, "NO_CONTACT_EMAIL")) {
        toast.error("Než účet zřídíme, doplňte prosím kontaktní e-mail podniku.");
        return;
      }
      toast.error("Účet se teď nepodařilo zřídit. Zkuste to prosím znovu.");
    },
  });

  function claimHandoff() {
    if (handoffPhaseNow.current !== "idle") return false;
    enterHandoffPhase("connecting");
    return true;
  }

  /** A navigation the browser cancelled (Stop, Escape, a blocked popup) fires
   *  no event this page can hear, so `leaving` would otherwise outlive the
   *  departure it waits for. The partner's own refresh releases it because no
   *  account request is in flight by then: a press after it costs at most one
   *  more single-use link, never a second account. `connecting` stays held. */
  function refetchAccount() {
    if (handoffPhaseNow.current === "leaving") enterHandoffPhase("idle");
    void accountQuery.refetch();
  }

  return {
    account: accountQuery.data ?? null,
    isLoading: isLoadingBusiness || accountQuery.isLoading,
    error: accountQuery.error,
    enabled: businessId !== null,
    refetch: refetchAccount,
    isStripeHandoffUnderway: handoffPhase !== "idle",
    startOnboarding: () => {
      if (claimHandoff()) startOnboarding.mutate();
    },
    createAccount: () => {
      if (claimHandoff()) createAccount.mutate();
    },
  };
}

/** The gate lastgo-api ADR 0004 draws across the portal: no tradeable account,
 *  no writing anything that ends in a customer paying. `error` is surfaced
 *  separately from `canTrade` because a failed request answers neither "can"
 *  nor "cannot" — collapsing it into `canTrade: false` reads to the partner as
 *  a Stripe refusal we never actually received. */
export function useCanTrade() {
  const { account, isLoading, error, refetch } = usePaymentAccount();
  return { canTrade: account?.canTrade ?? false, isLoading, error, refetch };
}
